/**
 * Email Templates Preview & Test (Super-Admin)
 * Preview all email templates with current branding, edit sample data, and send test emails.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@jourfx/convex/client";
import type { EmailTemplateName } from "@jourfx/convex/email/templates";
import {
  sampleData,
  templateGroups,
  templateLabels,
} from "@jourfx/convex/email/templates/sampleData";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Badge,
  useToast,
} from "@jourfx/ui";
import { Mail, Send, Eye, Info, Loader2 } from "lucide-react";
import { SuperAdminGuard } from "../components/SuperAdminGuard";
import { PageHeader } from "../components/PageHeader";
import { useSession } from "../lib/auth";

function getTemplateGroupLabel(
  t: (key: string) => string,
  groupKey: string,
  fallback: string
): string {
  switch (groupKey) {
    case "account":
      return t("groups.account");
    case "license":
      return t("groups.license");
    case "appointment":
      return t("groups.appointment");
    default:
      return fallback;
  }
}

// Union of all valid template names (matches the Convex action arg type)
type TemplateName =
  EmailTemplateName;

// ---------------------------------------------------------------------------
// Main content component
// ---------------------------------------------------------------------------

function EmailTemplatesContent() {
  const { t } = useTranslation("emailTemplates");
  const { t: tc } = useTranslation("common");
  const { toast } = useToast();
  const { data: session } = useSession();

  const previewEmail = useAction(api.email.index.previewEmail);
  const sendTestEmailAction = useAction(api.email.index.sendTestEmail);

  // State
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateName | "">("");
  const [templateData, setTemplateData] = useState<string>("");
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewText, setPreviewText] = useState<string>("");
  const [previewSubject, setPreviewSubject] = useState<string>("");
  const [showPlainText, setShowPlainText] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Default recipient to admin email
  useEffect(() => {
    if (session?.adminUser?.email && !recipientEmail) {
      setRecipientEmail(session.adminUser.email);
    }
  }, [recipientEmail, session?.adminUser?.email]);

  // Load sample data when template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateData("");
      setPreviewHtml("");
      setPreviewText("");
      setPreviewSubject("");
      return;
    }
    const data = sampleData[selectedTemplate as TemplateName];
    if (data) {
      setTemplateData(JSON.stringify(data, null, 2));
      setJsonError(null);
    }
  }, [selectedTemplate]);

  // Fetch preview (debounced)
  const fetchPreview = useCallback(
    async (template: TemplateName, dataStr: string) => {
      if (!template) return;

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(dataStr);
        setJsonError(null);
      } catch {
        setJsonError("Ungültiges JSON");
        return;
      }

      setIsLoading(true);
      try {
        const result = await previewEmail({
          template,
          data: parsed,
        });
        setPreviewHtml(result.html);
        setPreviewText(result.text);
        setPreviewSubject(result.subject);
      } catch (error) {
        toast({
          title: tc("error.title", "Fehler"),
          description:
            error instanceof Error ? error.message : tc("error.unknown", "Unbekannter Fehler"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [previewEmail, toast, tc],
  );

  // Debounced preview fetch when template or data changes
  useEffect(() => {
    if (!selectedTemplate || !templateData) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchPreview(selectedTemplate as TemplateName, templateData);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedTemplate, templateData, fetchPreview]);

  // Write HTML to iframe
  useEffect(() => {
    if (iframeRef.current && previewHtml && !showPlainText) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(previewHtml);
        doc.close();
      }
    }
  }, [previewHtml, showPlainText]);

  // Send test email
  const handleSendTest = async () => {
    if (!selectedTemplate || !recipientEmail) return;

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(templateData);
    } catch {
      toast({
        title: tc("error.title", "Fehler"),
        description: "Ungültiges JSON in den Vorlagendaten.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      await sendTestEmailAction({
        to: recipientEmail,
        template: selectedTemplate as TemplateName,
        data: parsed,
      });
      toast({
        title: t("send.title"),
        description: t("send.success"),
      });
    } catch (error) {
      toast({
        title: tc("error.title", "Fehler"),
        description:
          error instanceof Error ? error.message : t("send.error"),
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
      />

      {/* Branding info box */}
      <div className="flex items-start gap-3 rounded-lg border bg-muted/50 p-4">
        <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          {t("brandingNote")}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* Left column: selector + data editor + send */}
        <div className="space-y-6">
          {/* Template selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("templateSelector.label")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedTemplate}
                onValueChange={(value) => setSelectedTemplate(value as TemplateName)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("templateSelector.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {templateGroups.map((group) => (
                    <SelectGroup key={group.key}>
                      <SelectLabel>
                        {getTemplateGroupLabel(t, group.key, group.label)}
                      </SelectLabel>
                      {group.templates.map((tpl) => (
                        <SelectItem key={tpl} value={tpl}>
                          {templateLabels[tpl] || tpl}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Data editor */}
          {selectedTemplate && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t("dataEditor.title")}</CardTitle>
                <CardDescription>{t("dataEditor.description")}</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  className={`w-full rounded-md border bg-background px-3 py-2 font-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring ${
                    jsonError ? "border-destructive" : "border-input"
                  }`}
                  rows={14}
                  value={templateData}
                  onChange={(e) => setTemplateData(e.target.value)}
                  spellCheck={false}
                />
                {jsonError && (
                  <p className="mt-1 text-xs text-destructive">{jsonError}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Send test email */}
          {selectedTemplate && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t("send.title")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="recipientEmail">{t("send.emailLabel")}</Label>
                  <Input
                    id="recipientEmail"
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder={t("send.emailPlaceholder")}
                  />
                </div>
                <Button
                  onClick={handleSendTest}
                  disabled={isSending || !recipientEmail || !!jsonError}
                  className="w-full"
                >
                  {isSending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {isSending ? t("send.sending") : t("send.button")}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: preview */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t("preview.title")}</CardTitle>
              {selectedTemplate && (
                <div className="flex gap-1">
                  <Button
                    variant={showPlainText ? "ghost" : "secondary"}
                    size="sm"
                    onClick={() => setShowPlainText(false)}
                  >
                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                    {t("preview.html")}
                  </Button>
                  <Button
                    variant={showPlainText ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setShowPlainText(true)}
                  >
                    {t("preview.plainText")}
                  </Button>
                </div>
              )}
            </div>
            {previewSubject && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">{t("preview.subject")}:</span>
                <Badge variant="secondary" className="text-xs font-normal">
                  {previewSubject}
                </Badge>
              </div>
            )}
          </CardHeader>
          <CardContent className="flex-1 relative">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!selectedTemplate ? (
              // Empty state
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="mb-4 rounded-full bg-muted p-4">
                  <Mail className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("templateSelector.placeholder")}
                </p>
              </div>
            ) : showPlainText ? (
              <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-sm font-mono leading-relaxed min-h-[500px]">
                {previewText || " "}
              </pre>
            ) : (
              <iframe
                ref={iframeRef}
                title="Email preview"
                sandbox="allow-same-origin"
                className="w-full rounded-md border bg-white min-h-[500px]"
                style={{ height: "600px" }}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function EmailTemplates() {
  return (
    <SuperAdminGuard>
      <EmailTemplatesContent />
    </SuperAdminGuard>
  );
}
